import { Card } from '@deckee/deck-hand';
import { sleep } from '@deckee/utilities';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { callEndpoint, telemetry } from '../../../../services/backend';
import { captureEvent } from '../../../../services/event-capture';
import { openWithInAppBrowser } from '../../../../services/in-app-browser';
import { getLayerDefinitions } from '../../../../services/map-layer';
import {
  ROUTE_MARKER_COMMENT_READ,
  ROUTE_MARKER_READ,
  ROUTE_NEW_MARKER_COMMENT,
} from '../../../../services/route/route-names';
import {
  transitionToNextRoute,
  transitionToRawURL,
} from '../../../../services/route/route-navigation';
import {
  moveMapToLocation,
  setMarkerSummary,
  showPopover,
} from '../../../../services/store/modules/app-controls';
import { selectCurrentMember } from '../../../../services/store/modules/backend-service';
import { clearFollowModeActive } from '../../../../services/store/modules/log-trips';
import { selectMarkerTypeCategory } from '../../../../services/store/modules/marketplace';
import SheetLayout from '../../../layouts/SheetLayout/SheetLayout';
import LogoSpinner from '../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import MarkerSummary from '../MarkerSummary/MarkerSummary';

const treasureUrls = [
  '/maps/marker/summary/wharfMarker/171081',
  '/maps/marker/summary/boatRampMarker/197901',
  '/maps/marker/summary/fishingLocationMarker/44772',
  '/maps/marker/summary/marinaMarker/60917',
  '/maps/marker/summary/marinaMarker/50791',
  '/maps/marker/summary/pumpOutMarker/126975',
  '/maps/marker/summary/anchorageMarker/124922',
  '/maps/marker/summary/marinaMarker/50792',
  '/maps/marker/summary/boatRampMarker/5366',
  '/maps/marker/summary/fishingLocationMarker/32419',
];

const handleCallEndpoint = async (endpoint, params) => {
  // Attempt to get marker with exponential back-off.
  let data;
  let delay = 0;
  let error;
  while (!data && delay < 10000) {
    /* eslint-disable-next-line no-await-in-loop */
    await sleep(delay);
    try {
      /* eslint-disable-next-line no-await-in-loop */
      data = await callEndpoint(endpoint, params);
    } catch (currentError) {
      error = currentError;
    }
    delay = delay ? delay * 2 : 1000;
  }

  if (!data && error) {
    telemetry(
      telemetry.WARN,
      `[handleCallEndpoint] failed for endpoint: ${endpoint}`,
    );
    // If marker doesn't exist, behave like it's hidden
    return {
      hidden: true,
    };
  }
  return data;
};

const getItem = async (id, markerType, isMarketplaceListing) => {
  telemetry(telemetry.DEBUG, '[MarkerSummaryLoader.getItem] called');

  if (!id || !markerType) {
    return {
      title: 'Sorry, this page can’t be found',
    };
  }

  // Get the item
  const item = await handleCallEndpoint(
    callEndpoint.endpoints.mapSearchQueryById,
    { id },
  );

  if (item.hidden === true) {
    // If marker is hidden, send back to map
    return {
      title: 'Sorry, this page can’t be found',
    };
  }
  telemetry(
    telemetry.DEBUG,
    `[MarkerSummaryLoader.getItem] received item ${JSON.stringify(item)}`,
  );

  // Get our marker summary config
  const markerSummaryConfig = await getLayerDefinitions()[
    markerType
  ].popOverConfiguration(item);

  let story;

  if (markerType === 'storyMarker') {
    const { stories } = await handleCallEndpoint(
      callEndpoint.endpoints.getMemberStories,
      { id: item.owningInfluencer.id },
    );
    story = stories;
    telemetry(
      telemetry.DEBUG,
      `[MarkerSummaryLoader.getItem] received story ${JSON.stringify(story)}`,
    );
  }
  // Format the display variables
  const displayChat = !!markerSummaryConfig?.displayChat;
  const title = markerSummaryConfig?.title || '';
  const description = story?.title || item?.title || '';
  const commentCount = item?.numberOfComments || null;

  // Get the banner & source
  const banner = item?.content?.banner;

  // Check for a popover button override
  let popoverButton = null;
  let buttonLabel = null;
  let buttonHref = null; // () => transitionToNextRoute(ROUTE_MARKER_READ, { markerType, id })
  let buttonAction;
  const links = item?.content?.links;
  if (links?.length > 0) {
    popoverButton = links.find((link) => link.type === 'popoverButtonOverride');
  }

  // Setup the button href or action
  if (popoverButton && popoverButton.url) {
    buttonLabel = popoverButton.label;
    buttonHref = popoverButton.url;

    if (markerType === 'webcamMarker') {
      buttonAction = (event) => {
        event.preventDefault();
        captureEvent({
          type: 'marker summary: click primary cta',
          payload: { markerType, id },
        });
        openWithInAppBrowser({ url: buttonHref });
      };
    }
  }

  if (!popoverButton) {
    buttonAction = () => {
      captureEvent({
        type: 'marker summary: click primary cta',
        payload: { markerType, id },
      });
      // If marker is part of a category, navigate to the listing view
      if (isMarketplaceListing) {
        return transitionToRawURL(`/maps/marketplace/listing/${id}`);
      }
      return transitionToNextRoute(ROUTE_MARKER_READ, { markerType, id });
    };
  }

  // Setup the share and comment actions
  const markerSharingPath = `marker/summary/${markerType}/${id}`;
  const markerSharingText = markerSummaryConfig?.shareText
    ? markerSummaryConfig.shareText
    : `Check out this ${markerSummaryConfig?.title} on Deckee!`;

  const readCommentsAction = () =>
    transitionToNextRoute(ROUTE_MARKER_COMMENT_READ, { markerType, id });
  const addCommentsAction = () =>
    transitionToNextRoute(ROUTE_NEW_MARKER_COMMENT, { markerType, id });

  // Format the lat and lon
  const formattedLat = item?.latLon?.lat.toFixed(4);
  const formattedLon = item?.latLon?.lon.toFixed(4);

  return {
    formattedLatLon: {
      lat: +formattedLat,
      lon: +formattedLon,
    },
    title,
    description,
    banner,
    content: item?.content,
    buttonLabel,
    buttonHref,
    buttonAction,
    displayChat,
    commentCount,
    readCommentsAction,
    addCommentsAction,
    markerSharingPath,
    markerSharingText,
  };
};

const MarkerSummaryLoader = ({
  id,
  markerType,
  dispatchShowPopover,
  dispatchMoveMapToLocation,
  dispatchClearFollowModeActive,
  dispatchResetBearingAndPitch,
  dispatchSetMarkerSummary,
}) => {
  const markerHasAMarketplaceCategory = useSelector((s) =>
    selectMarkerTypeCategory(s, markerType),
  );

  const { email } = useSelector(selectCurrentMember);

  const location = useLocation();

  useEffect(() => {
    const treasureUrl = treasureUrls.find((url) =>
      location.pathname.includes(url),
    );

    if (treasureUrl) {
      openWithInAppBrowser({
        url: `https://deckee.typeform.com/to/ROxVKDsh#email=${email}`,
      });
    }
  }, [email, location.pathname]);

  const [data, setData] = useState();
  const [loading, setLoading] = useState(false);

  // this feels really hacky. This might be better as a thunk.
  useEffect(() => {
    dispatchClearFollowModeActive();
    // let isMounted = true;
    const loadData = async () => {
      try {
        setLoading(true);
        const apiData = await getItem(
          id,
          markerType,
          markerHasAMarketplaceCategory,
        );
        setData(apiData);
        setLoading(false);

        dispatchMoveMapToLocation({
          center: {
            lat: apiData.formattedLatLon.lat,
            lon: apiData.formattedLatLon.lon,
          },
          // zoom: 12,
        });
      } catch (err) {
        // we do nothing with this error and we should do something.
        telemetry(
          telemetry.DEBUG,
          'Error caught in MarkerSummaryLoader.useEffect',
          err,
        );
        // if (isMounted) {
        //   setError(err);
        // }
      }
    };

    loadData();
  }, [
    id,
    markerType,
    dispatchClearFollowModeActive,
    dispatchMoveMapToLocation,
    dispatchResetBearingAndPitch,
    dispatchSetMarkerSummary,
    dispatchShowPopover,
    markerHasAMarketplaceCategory,
  ]);

  return (
    <SheetLayout
      heading={data?.content?.customSummaryTitle || data?.title || ''}
      headingPosition="left"
      showMenuBar={false}
    >
      <Card bg="transparent">
        <Card.Body pt="0">
          {loading && <LogoSpinner />}
          {data && (
            <MarkerSummary
              id={id}
              latLon={data.formattedLatLon}
              title={data?.content?.customSummaryTitle || data?.title || ''}
              description={data.description}
              banner={data.banner}
              commentCount={data.commentCount}
              displayChat={data.displayChat}
              buttonHref={data.buttonHref}
              buttonAction={data.buttonAction}
              readCommentsAction={data.readCommentsAction}
              addCommentsAction={data.addCommentsAction}
              shareText={data.markerSharingText}
              sharePath={data.markerSharingPath}
              markerType={markerType}
              image={data?.content?.iconImage}
              eventDetails={data?.content?.eventDetails}
            />
          )}
        </Card.Body>
      </Card>
    </SheetLayout>
  );
};

MarkerSummaryLoader.propTypes = {
  dispatchShowPopover: PropTypes.func.isRequired,
  dispatchClearFollowModeActive: PropTypes.func.isRequired,
  dispatchResetBearingAndPitch: PropTypes.func.isRequired,
  dispatchSetMarkerSummary: PropTypes.func.isRequired,
  markerType: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  dispatchShowPopover: (popoverConfig) => dispatch(showPopover(popoverConfig)),
  dispatchMoveMapToLocation: async (newLocation) =>
    dispatch(await moveMapToLocation(newLocation)),
  dispatchClearFollowModeActive: () => dispatch(clearFollowModeActive()),
  dispatchResetBearingAndPitch: async () =>
    dispatch(await moveMapToLocation({ bearing: 0, pitch: 0 })),
  dispatchSetMarkerSummary: (markerType, selectedMarkerId) =>
    dispatch(
      setMarkerSummary({
        markerType,
        selectedMarkerId,
      }),
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(MarkerSummaryLoader);
